import { useTranslation } from '@dreamstack/i18n'
import { ChevronDown, ChevronUp } from '@dreamstack/icons'
import type { PropertyPreview } from '@dreamstack/web/lib/prisma/properties'
import { InfoBox, useJsApiLoader } from '@react-google-maps/api'
import Image from 'next/image'
import type { FunctionComponent } from 'react'
import { useState } from 'react'
import tw, { styled } from 'twin.macro'
import { ScreenBreakpoints, useIsMdOrGreater, useWindowSize } from '../../index'
import { MyMap } from './MyMap'
import type { MapStyleFeature } from './mapStyle'
import { mapStyle as initialMapStyle } from './mapStyle'
import { parseGeoLocation } from './parseGeoLocation'

const ImageSrcPrefix = '/static/images/mapIcons/'

const HOME_MARKER_SIZES = {
  md: 44,
  lg: 70,
}

type Category = {
  featureType: string
  elementType?: string
  value: string
}

const initialCategories: Category[] = []
initialMapStyle.forEach((block) => {
  const stylers = block.stylers
  let categoryValue = undefined
  stylers.forEach((styler) => {
    if (styler['visibility']) {
      categoryValue = styler['visibility']
    }
  })

  if (categoryValue) {
    initialCategories.push({
      featureType: block.featureType,
      elementType: block.elementType,
      value: categoryValue,
    })
  }
})

// Containers
const PlacesContainer = styled.div<{ $loading?: boolean }>(({ $loading }) => [
  tw`font-montserrat bg-accentroWhite-full
  md:(px-px-20) md:(pt-px-20) md:(pb-px-8) lg:(px-px-32) lg:(pt-px-32) lg:(pb-px-8) 
  position[absolute] top[32px] left[24px] md:(top[33px]) md:(left[48px]) lg:(top[64px]) lg:(left[48px])
  border boxShadow[0 1px 4px 0 rgba(138, 138, 138, 0.5)]
  z-index[100]`,
  $loading && tw`hidden`,
])
const PlacesContainerMobile = styled.div<{ $loading?: boolean }>(
  ({ $loading }) => [
    tw`font-montserrat
  position[absolute] top[32px] left[24px]
  z-index[100]`,
    $loading && tw`hidden`,
  ]
)

// Titles
const PlacesTitle = tw.div`flex font-montserrat font-semibold mb-px-16`
const PlacesTitleMobile = tw.div`
  p-px-8 width[311px]
  flex bg-accentroWhite-full font-montserrat font-semibold mb-px-4
  border boxShadow[0 1px 4px 0 rgba(138, 138, 138, 0.5)]
 `

// Items Containers
const PlacesItemsContainer = tw.div``
const PlacesItemsContainerMobile = tw.div`
  bg-accentroWhite-full 
  p-px-16 mt-px-12 
  border boxShadow[0 1px 4px 0 rgba(138, 138, 138, 0.5)]
`

// Misc
const PlacesTitleText = tw.div`pb-px-4 select-none`
const PlacesTitleTextMobile = tw.div`pt-px-4 select-none cursor-pointer`

const PlacesTitleCloseButton = tw.div`display[inline] md:(display[none]) font-montserrat font-semibold mr-px-4 cursor-pointer`

export const PropertyMap: FunctionComponent<
  React.PropsWithChildren<{
    property: PropertyPreview
    loading?: boolean
  }>
> = ({ property }) => {
  const t = useTranslation()
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!,
  })
  const DISPLAY_CATEGORIES: Record<string, string> = {
    'poi.attraction': t('accentro:attractions'),
    'poi.business': t('accentro:business'),
    'poi.medical': t('accentro:medical'),
    'poi.school': t('accentro:schools'),
    'poi.sports_complex': t('accentro:sport'),
  }

  const { width } = useWindowSize()
  const buttonIconSize =
    width && width < ScreenBreakpoints.breakpointLg ? 24 : 34
  const homeMarkerSize =
    width && width < ScreenBreakpoints.breakpointLg
      ? HOME_MARKER_SIZES.md
      : HOME_MARKER_SIZES.lg
  const isLargerThanSmall = useIsMdOrGreater()

  const [poiCategories, setPoiCategories] = useState(initialCategories)
  const [forceShowLegend, setForceShowLegend] = useState(false)

  const latLng = parseGeoLocation(property?.geoLocation)

  if (!latLng) {
    return null
  }

  const togglers = poiCategories.map((category, ind) => {
    const clickHandler = () => {
      setPoiCategories((prevList: Category[]) => {
        const newList: Category[] = prevList.slice(0)
        newList.forEach((elem: Category) => {
          if (
            elem.featureType === category.featureType &&
            elem.elementType === category.elementType
          ) {
            elem.value = elem.value === 'on' ? 'off' : 'on'
          }
        })
        return newList
      })
    }

    return (
      <div key={ind} tw="flex mb-px-8">
        <span tw="cursor-pointer">
          <Image
            src={
              ImageSrcPrefix +
              category.featureType +
              (category.value == 'on' ? '.Selected' : '') +
              '.svg'
            }
            onClick={clickHandler}
            alt={DISPLAY_CATEGORIES[category.featureType] + ' Icon'}
            width={buttonIconSize}
            height={buttonIconSize}
          />
        </span>
        <button
          tw="ml-px-16 pb-px-6"
          onClick={clickHandler}
          style={{
            fontWeight: category.value == 'on' ? 'bold' : 'inherit',
          }}
        >
          {DISPLAY_CATEGORIES[category.featureType]}
        </button>
      </div>
    )
  })

  const mapStyle: MapStyleFeature[] = initialMapStyle.slice(0)
  mapStyle.forEach((block: MapStyleFeature) => {
    poiCategories.forEach((category) => {
      if (
        block.featureType === category.featureType &&
        block.elementType === category.elementType
      ) {
        block.stylers[0].visibility = category.value
      }
    })
  })

  const toggleLegendStatus = () => {
    setForceShowLegend((oldValue) => !oldValue)
  }

  return (
    <div tw="relative mb-px-24">
      {!isLargerThanSmall && (
        <PlacesContainerMobile $loading={!isLoaded}>
          <PlacesTitleMobile>
            <PlacesTitleCloseButton>
              {!isLargerThanSmall && forceShowLegend && (
                <ChevronUp onClick={toggleLegendStatus} />
              )}
              {!isLargerThanSmall && !forceShowLegend && (
                <ChevronDown onClick={toggleLegendStatus} />
              )}
            </PlacesTitleCloseButton>
            <PlacesTitleTextMobile onClick={toggleLegendStatus}>
              {t('accentro:nearbyPlaces')}
            </PlacesTitleTextMobile>
          </PlacesTitleMobile>
          {forceShowLegend && (
            <PlacesItemsContainerMobile>{togglers}</PlacesItemsContainerMobile>
          )}
        </PlacesContainerMobile>
      )}
      {isLargerThanSmall && (
        <PlacesContainer $loading={!isLoaded}>
          <PlacesTitle>
            <PlacesTitleCloseButton>
              {!isLargerThanSmall && forceShowLegend && (
                <ChevronUp
                  onClick={() => {
                    setForceShowLegend(false)
                  }}
                />
              )}
              {!isLargerThanSmall && !forceShowLegend && (
                <ChevronDown
                  onClick={() => {
                    setForceShowLegend(true)
                  }}
                />
              )}
            </PlacesTitleCloseButton>
            <PlacesTitleText>{t('accentro:nearbyPlaces')}</PlacesTitleText>
          </PlacesTitle>
          {(isLargerThanSmall || forceShowLegend) && (
            <PlacesItemsContainer>{togglers}</PlacesItemsContainer>
          )}
        </PlacesContainer>
      )}
      <MyMap center={latLng} mapStyle={mapStyle} isLoaded={isLoaded}>
        {typeof window !== 'undefined' && isLoaded && (
          <InfoBox
            options={{
              boxStyle: {
                border: '1px solid rgba(0,0,0,0)',
              },
              closeBoxURL: '',
              enableEventPropagation: false,
              disableAutoPan: true,
              pixelOffset: new google.maps.Size(
                -homeMarkerSize / 2,
                -homeMarkerSize / 2
              ),
            }}
            position={new google.maps.LatLng(latLng.lat, latLng.lng)}
          >
            <Image
              width={homeMarkerSize}
              height={homeMarkerSize}
              src={ImageSrcPrefix + 'home.svg'}
              alt={'Property location marker'}
            />
          </InfoBox>
        )}
      </MyMap>
    </div>
  )
}
